import type { ConfigStepKey, ProtocolGroup } from '@/lib/configuratorSchema';
import {
  parseStepCopyOverrides,
  serializeStepCopyOverrides,
  type StepCopyOverrides,
} from '@/lib/configuratorStepCopy';

export type { ConfigStepKey as SubtypeConfigStep };
export type PricingSchema = {
  formula?: string;
  base_price?: number;
  matrix?: Array<Record<string, unknown>>;
  lookup?: Record<string, number>;
  dim?: string;
};

export type SubtypeOptionSchema = {
  steps?: ConfigStepKey[];
  protocols?: string[];
  colors?: string[];
  control_tags?: string[];
  profile_colors?: string[];
  motor_colors?: string[];
  protocol_groups?: ProtocolGroup[];
  dim_label?: string;
  tile_hint?: string;
  step_copy?: StepCopyOverrides;
  pricing?: PricingSchema;
  image_url?: string;
};

export const SUBTYPE_SETUP_STEPS: {
  key: 'vitrine' | 'flow' | 'params' | 'price' | 'data';
  label: string;
  subtitle: string;
  accent: 'cyan' | 'violet' | 'amber' | 'slate' | 'emerald';
}[] = [
  { key: 'vitrine', label: 'Витрина', subtitle: 'Плитка «Прямой» в каталоге', accent: 'cyan' },
  { key: 'flow', label: 'Калькулятор', subtitle: 'Шаги и тексты блоков', accent: 'violet' },
  { key: 'params', label: 'Параметры', subtitle: 'Протоколы, цвета, размер', accent: 'amber' },
  { key: 'price', label: 'Цена', subtitle: 'Формула без SKU', accent: 'slate' },
  { key: 'data', label: 'Данные', subtitle: 'SKU и привязки', accent: 'emerald' },
];

export const ALL_SUBTYPE_CONFIG_STEPS: { key: ConfigStepKey; label: string; icon: string; hint: string }[] = [
  { key: 'tier', label: 'Профиль', icon: '⭐', hint: 'Standard / Premium' },
  { key: 'power', label: 'Питание', icon: '⚡', hint: 'АКБ / 220В' },
  { key: 'opening', label: 'Открывание', icon: '↔️', hint: 'Левое / правое' },
  { key: 'control', label: 'Управление', icon: '🎛️', hint: 'Протокол и фазы' },
  { key: 'iface', label: 'Интерфейс', icon: '🔌', hint: 'Legacy-шаг' },
  { key: 'protocol', label: 'Протокол', icon: '📶', hint: 'Legacy-шаг' },
  { key: 'color', label: 'Цвет', icon: '🎨', hint: 'Legacy один цвет' },
  { key: 'profile_color', label: 'Цвет профиля', icon: '🖼️', hint: 'Корпус карниза' },
  { key: 'motor_color', label: 'Цвет мотора', icon: '⚙️', hint: 'Привод' },
  { key: 'size', label: 'Размер', icon: '📏', hint: 'Прайс-матрица' },
];

export function subtypeSchemaToForm(raw: Record<string, unknown>): SubtypeOptionSchema {
  return {
    steps: Array.isArray(raw.steps) ? (raw.steps as ConfigStepKey[]) : [],
    protocols: Array.isArray(raw.protocols) ? (raw.protocols as string[]) : [],
    colors: Array.isArray(raw.colors) ? (raw.colors as string[]) : [],
    control_tags: Array.isArray(raw.control_tags) ? (raw.control_tags as string[]) : [],
    profile_colors: Array.isArray(raw.profile_colors) ? (raw.profile_colors as string[]) : [],
    motor_colors: Array.isArray(raw.motor_colors) ? (raw.motor_colors as string[]) : [],
    protocol_groups: Array.isArray(raw.protocol_groups) ? (raw.protocol_groups as ProtocolGroup[]) : [],
    dim_label: typeof raw.dim_label === 'string' ? raw.dim_label : '',
    tile_hint: typeof raw.tile_hint === 'string' ? raw.tile_hint : '',
    step_copy: parseStepCopyOverrides(raw),
    pricing:
      raw.pricing && typeof raw.pricing === 'object' && !Array.isArray(raw.pricing)
        ? (raw.pricing as PricingSchema)
        : undefined,
    image_url: typeof raw.image_url === 'string' ? raw.image_url : undefined,
  };
}

export function subtypeFormToSchema(f: SubtypeOptionSchema): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  if (f.steps?.length) o.steps = f.steps;
  if (f.protocols?.length) o.protocols = f.protocols;
  if (f.colors?.length) o.colors = f.colors;
  if (f.control_tags?.length) o.control_tags = f.control_tags;
  if (f.profile_colors?.length) o.profile_colors = f.profile_colors;
  if (f.motor_colors?.length) o.motor_colors = f.motor_colors;
  if (f.protocol_groups?.length) o.protocol_groups = f.protocol_groups;
  if (f.dim_label?.trim()) o.dim_label = f.dim_label.trim();
  if (f.tile_hint?.trim()) o.tile_hint = f.tile_hint.trim();
  const stepCopy = serializeStepCopyOverrides(f.step_copy);
  if (stepCopy) o.step_copy = stepCopy;
  if (f.pricing && Object.keys(f.pricing).length > 0) o.pricing = f.pricing;
  if (f.image_url?.trim()) o.image_url = f.image_url.trim();
  return o;
}
